import { memo, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOutlet } from "react-router-dom";
import { Form } from "../../common/components";
import { useForm } from "../../common/hooks/useForm";
import { usePageTitle } from "../../common/hooks/usePageTitle";
import { useSelector } from "../../common/hooks/useSelector";
import { SpecsContext } from "../../context";
import {
  AddressFormType,
  ClientDetails,
  PreOrderPaymentOptions,
} from "../../types";
import { useCart } from "../../utils/CartHooks";
import { useGlobalLoading } from "../../utils/ContextHooks";
import CartPreview from "./CartPreview/CartPreview";
import { CartSchema } from "./CartSchema";
import { CheckoutTemplate } from "./CheckoutTemplate";
import EmptyCart from "./EmptyCart";
import { CheckoutTemplateSkeleton } from "./Skeleton/CheckoutTemplateSkeleton";
import { getEmptyAddress } from "../../utils/userAddress.utils";
import { useAuth } from "../../common/hooks/useAuth";
import { productNameToProductLineAndFamily } from "../../utils/product.utils";
import { trackCartViewed } from "../../utils/analytics.utils";
import { getReducedCartItems } from "../../utils/CartUtils";

// !!! IMPORTANT IF YOU WANT TO BRING BACK STRIPE !!!
// FIXME: See commit e71a4964daa11de3ec441d308b396182da198f6a to see how the
//  stripe checkout was managed before being removed.

export type CheckoutForm = {
  shippingAddress: AddressFormType;
  billingAddress: AddressFormType;
  clientDetails: ClientDetails;
  termsAndConditionsAccepted: boolean;
  finalDesignValidated: boolean;
  poNumber: string;
  notes?: string;
  carbonNeutral?: boolean;
  paymentOption?: PreOrderPaymentOptions;
};

const Cart = () => {
  const { country } = useSelector((state) => state.userInfo);
  const { isDistributor, isAdmin } = useAuth();
  const [isCartViewed, setIsCartViewed] = useState(false);
  const { i18n } = useTranslation();
  const outlet = useOutlet();
  const { loading: specsLoading } = useContext(SpecsContext);
  usePageTitle("pages.cart.title");
  const tEnglish = i18n.getFixedT("en");
  const userInfos = useSelector((state) => state.userInfo);

  const { isGloballyLoading } = useGlobalLoading();

  const { fetchCartItemsAndUpdateStore } = useCart();
  const {
    cart,
    loading: cartLoading,
    loaded,
    showDistributorPrices,
  } = useSelector((state) => state.cart);

  const formik = useForm({
    initialValues: {
      shippingAddress: getEmptyAddress(country),
      billingAddress:
        isDistributor || isAdmin
          ? { ...getEmptyAddress(country), id: "default" }
          : { ...getEmptyAddress(country), id: "default-shipping" },
      termsAndConditionsAccepted: false,
      finalDesignValidated: false,
      poNumber: "",
      notes: "",
      clientDetails: undefined,
      // carbonNeutral: cart.carbonNeutral,
    },
    onSubmit: () => undefined,
    validationSchema: CartSchema(),
    validateOnChange: true,
    validateOnMount: true,
  });

  useEffect(() => {
    fetchCartItemsAndUpdateStore();
  }, [fetchCartItemsAndUpdateStore]);

  useEffect(() => {
    if (isDistributor || isAdmin) {
      formik.setFieldValue("billingAddress", {
        ...getEmptyAddress(country),
        id: "default",
      });
    }
    // eslint-disable-next-line
  }, [isDistributor, isAdmin, country]);

  // useEffect(() => {
  //   formik.setFieldValue("carbonNeutral", cart.carbonNeutral);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [cart.carbonNeutral]);

  // Track cart viewed only once on initial load
  useEffect(() => {
    if (!isCartViewed && loaded && cart?.comboItems?.length > 0) {
      const data = {
        cart_id: userInfos.id,
        products: getReducedCartItems(cart?.comboItems).map((itemPricing) => {
          const { family, productLine } = productNameToProductLineAndFamily(
            itemPricing.productName
          );
          const pricePerUnit =
            showDistributorPrices && itemPricing.distributor?.pricePerUnit
              ? itemPricing.distributor?.pricePerUnit
              : itemPricing.pricePerUnit;
          return {
            product_id: itemPricing.productName,
            sku: itemPricing.designId,
            name: itemPricing.productName,
            category: family?.labelTag ? tEnglish(family?.labelTag) : "",
            brand: tEnglish(`line.${productLine}.title`),
            price: pricePerUnit,
            carbon_neutral_fee: itemPricing.carbonNeutralityFeeTotal,
            quantity: itemPricing.quantity,
          };
        }),
      };
      if (data) {
        setIsCartViewed(true);
        trackCartViewed(data);
      }
    }
  }, [
    loaded,
    cart,
    isCartViewed,
    userInfos.id,
    showDistributorPrices,
    tEnglish,
  ]);

  if (!outlet) {
    return null;
  }

  if (
    (!cart.comboItems?.length && cartLoading) ||
    specsLoading ||
    isGloballyLoading
  ) {
    return <CheckoutTemplateSkeleton />;
  }

  if (!cart.comboItems?.length) {
    return <EmptyCart />;
  }

  return (
    <Form formik={formik} formProps={{ style: { width: "100%" } }}>
      <CheckoutTemplate leftPanel={outlet} rightPanel={<CartPreview />} />
    </Form>
  );
};

export default memo(Cart);
